// Tour step properties:
//   - aboveDrawer: (bool) step targets an element inside a drawer overlay — adjusts z-index accordingly
//   - awaitClick: (string) CSS selector — the tour pauses until the user clicks this element
//   - awaitClickAdvanceDelay: (number) ms to wait after the click before moving to the next step
//   - awaitClickAll: (bool) listen for click on ALL elements matching awaitClick, not just the first
//   - awaitClickDelegate: (bool) use event delegation for awaitClick — resilient to DOM re-renders (e.g. FullCalendar)
//   - awaitClickLast: (bool) target the last matching element and block clicks on all others
//   - blockClickOnlySelectors: (string[]) CSS selectors whose click events are blocked (mousedown allowed for drag-and-drop)
//   - blockClickSelectors: (string[]) CSS selectors whose click AND mousedown events are blocked
//   - blockTarget: (bool|string) true = block clicks on the spotlight target; string = block clicks on that selector
//   - blockTargetException: (string|string[]) inside a blocked target, these selectors stay clickable
//   - blockTargetSiblings: (bool) block all siblings inside the target container, only the awaitClick element stays clickable
//   - clickBeforeNextIf: (object|object[]) one or more { selector, containsText?, clickSelector?, delayAfter? } — before advancing, click element if text matches
//   - clickBeforeTarget: (string|string[]) click the first found selector before waiting for step target (e.g. switch a network tab); see also clickBeforeTargetDelay
//   - clickBeforeTargetDelay: (number) ms to wait after clickBeforeTarget before starting to wait for the target (default: 300)
//   - clickOnFound: (string|string[]) click the first found selector right when the target is detected, before exitIfMissing checks; use this when the click must happen after async content loads (e.g. video upload) to avoid being overridden by app auto-switching
//   - congrats: (bool) marks the final congratulatory step (triggers confetti)
//   - content: (string) i18n key for the step body text
//   - exitIfMissing: (object|object[]) { selector, toStep } or array — after target found, check each condition in order; jump to toStep on first absent selector
//   - hideFinish: (bool) hide the Finish button (used when chaining to another tour)
//   - hidePrev: (bool) hide the Previous button for this step
//   - highlightDays: (bool) add a pulsing highlight to calendar day cells
//   - lockScroll: (string|string[]) CSS selector(s) of scroll container(s) to lock while this step is active — prevents user scroll from drifting the spotlight off its target (programmatic scroll still works)
//   - nextTour: (string) id of the tour to start when this step or tour ends (chaining)
//   - nextToStep: (number) step index to jump to when user clicks "Suivant" instead of stepIndex + 1
//   - noSpotlight: (bool) disable the spotlight cutout (full overlay, e.g. for congrats)
//   - position: (string) tooltip placement relative to the target — top | bottom | left | right | center
//   - prevToStep: (number) step index to jump to when user clicks "Précédent" instead of stepIndex - 1
//   - pulse: (bool) show a pulsing animation on the awaitClick element (default: true when awaitClick is set)
//   - raiseAncestors: (bool) raise ancestor stacking contexts so the target escapes its parent z-index
//   - scrollTo: (string) 'top' scrolls the page to top instead of centering the target
//   - scrollToBottom: (bool) force the scroll container to its absolute bottom — use when the target is the last element of a long form and scrollIntoView cannot reliably reach it
//   - scrollContainer: (string) CSS selector of the scroll container to use with scrollTo: 'bottom' — avoids DOM traversal heuristic for reliable scroll in nested overflow containers
//   - skipIfMissing: (bool) auto-advance (or go back) when the step target is not found within timeout
//   - skipIfMissingToStep: (number) like skipIfMissing but jumps to this specific step index (forward direction only)
//   - skipToStep: (number) step index to jump to when user clicks "Passer cette étape" — only shown when awaitClick is set
//   - spotlightPadding: (number|object) extra px around the target — number for uniform, object { top, right, bottom, left } for per-side
//   - target: (string) CSS selector of the element to highlight
//   - title: (string) i18n key for the step title
//   - tooltipTarget: (string) CSS selector used to position the tooltip independently from the spotlight target
//   - watchTargets: (string[]) extra CSS selectors included in the spotlight area

// Full step API: see applyStep() in TourOverlay.vue
//
// Step flow:
//   [0-3]  Intro — overview, schedule, content, toolbar
//   [4]    Media upload → video: [5]→[13]  |  photo: [6]→[7-12]→[13]  |  skip: [13]
//   [13-14] Comment + advanced settings
//   [15]   Music button (video + native TikTok/IG only) → skipIfMissing: [18]
//   [16-17] Music overlay
//   [18-23] Phone preview + publish (chains to plannerReturn)

export default {
  id: 'postCreator',
  route: 'postCreator',
  injectContent: {
    text: 'Nous g\u00e9rons tous nos r\u00e9seaux sociaux avec Mashup Web Social ! Planification, mod\u00e9ration et reporting en un seul outil. Essayez-le ! #MashupWebSocial #SocialMedia #CommunityManagement'
  },
  steps: [
    // [0] Overview
    {
      target: '.section-post-creator',
      title: 'tours.postCreator.steps.overview.title',
      content: 'tours.postCreator.steps.overview.content',
      position: 'center',
      spotlightPadding: 40,
      blockTarget: true,
      blockOutsideSpotlight: true
    },
    // [1] Schedule date
    {
      target: '.publish-date',
      title: 'tours.postCreator.steps.schedule.title',
      content: 'tours.postCreator.steps.schedule.content',
      position: 'right',
      watchTargets: ['.publish-date .listOptions', '.publish-date .input-hours', '.publish-date .vue__time-picker .dropdown'],
      lockScroll: '.container-form-scroll',
      blockOutsideSpotlight: true
    },
    // [2] Content editor
    {
      target: '.content-post',
      title: 'tours.postCreator.steps.content.title',
      content: 'tours.postCreator.steps.content.content',
      position: 'right',
      watchTargets: ['.content-post .emoji-mart', '.form-overlay.select-mention'],
      lockScroll: '.container-form-scroll',
      blockOutsideSpotlight: true
    },
    // [3] Editor toolbar
    {
      target: '.content-post',
      title: 'tours.postCreator.steps.editorToolbar.title',
      content: 'tours.postCreator.steps.editorToolbar.content',
      position: 'bottom',
      watchTargets: ['.content-post .emoji-mart'],
      lockScroll: '.container-form-scroll',
      blockOutsideSpotlight: true
    },
    // [4] Media upload — branches on result:
    //     video: [5]→[13]  |  photo: [6]→[7-12]→[13]  |  skip: [13]
    {
      target: '.content-media',
      title: 'tours.postCreator.steps.media.title',
      content: 'tours.postCreator.steps.media.content',
      position: 'right',
      spotlightPadding: { top: 8, right: -180, bottom: 8, left: 8 },
      tooltipTarget: '.btn-add-media',
      awaitClick: '.btn-add-media',
      // Skip directly to step 6 "Premier commentaire" [13]
      skipToStep: 13,
      lockScroll: '.container-form-scroll',
      blockOutsideSpotlight: true
    },
    // [5] Media: video preview — waitForTarget, branches on upload result:
    //     clickOnFound: switch tab to TikTok/IG when video detected (fires after upload, overrides auto-switch)
    //     exitIfMissing[0]: photo → [6] | exitIfMissing[1]: video, no native TikTok/IG → [13]
    //     skipIfMissingToStep [6]: no media after timeout | nextToStep [13]: no ImageCropper for video
    {
      target: '.media-container',
      title: 'tours.postCreator.steps.mediaVideoPreview.title',
      content: 'tours.postCreator.steps.mediaVideoPreview.content',
      position: 'right',
      tooltipTarget: '.media-container',
      waitForTarget: true,
      clickOnFound: ['.nav-type-network .network.tiktok', '.nav-type-network .network.instagram'],
      clickOnFoundRequires: '.media-container.video',
      exitIfMissing: [
        { selector: '.media-container.video', toStep: 6 },
        { selector: '.nav-type-network .network.tiktok, .nav-type-network .network.instagram', toStep: 13 }
      ],
      skipIfMissingToStep: 6,
      nextToStep: 13,
      hidePrev: true,
      blockClickSelectors: ['.media-container.video .media.video .preview'],
      lockScroll: '.container-form-scroll',
      blockOutsideSpotlight: true
    },
    // [6] Media: click on the uploaded photo
    {
      target: '.content-media',
      title: 'tours.postCreator.steps.mediaClick.title',
      content: 'tours.postCreator.steps.mediaClick.content',
      position: 'right',
      spotlightPadding: { top: -10, right: -180, bottom: 8, left: -118 },
      tooltipTarget: '.media-container',
      awaitClick: '.content-media .media-container.picture',
      awaitClickDelegate: true,
      blockClickSelectors: ['.content-media .fa-circle-xmark'],
      skipToStep: 13,
      lockScroll: '.container-form-scroll',
      blockOutsideSpotlight: true
    },
    // [7] ImageCropper: overview
    {
      target: '.imageCropper',
      title: 'tours.postCreator.steps.imageCropperOverview.title',
      content: 'tours.postCreator.steps.imageCropperOverview.content',
      position: 'center',
      aboveDrawer: true,
      blockTarget: true,
      spotlightPadding: 20,
      hidePrev: true,
      blockOutsideSpotlight: true
    },
    // [8] ImageCropper: crop canvas
    {
      target: '.containerVueCropper',
      title: 'tours.postCreator.steps.imageCropperCanvas.title',
      content: 'tours.postCreator.steps.imageCropperCanvas.content',
      position: 'top',
      aboveDrawer: true,
      blockTarget: true,
      blockOutsideSpotlight: true
    },
    // [9] ImageCropper: ratio
    {
      target: '.containerMenuRatio',
      title: 'tours.postCreator.steps.imageCropperRatio.title',
      content: 'tours.postCreator.steps.imageCropperRatio.content',
      position: 'right',
      aboveDrawer: true,
      blockTarget: false,
      blockOutsideSpotlight: true
    },
    // [10] ImageCropper: rotate & flip
    {
      target: '.header-image-cropper .options',
      title: 'tours.postCreator.steps.imageCropperRotateFlip.title',
      content: 'tours.postCreator.steps.imageCropperRotateFlip.content',
      position: 'bottom',
      aboveDrawer: true,
      blockTarget: false,
      blockOutsideSpotlight: true
    },
    // [11] ImageCropper: reset
    {
      target: '.btn-reset',
      title: 'tours.postCreator.steps.imageCropperReset.title',
      content: 'tours.postCreator.steps.imageCropperReset.content',
      position: 'right',
      aboveDrawer: true,
      blockTarget: false,
      blockOutsideSpotlight: true
    },
    // [12] ImageCropper: confirm (closes cropper)
    {
      target: '.btn-finish',
      title: 'tours.postCreator.steps.imageCropperFinish.title',
      content: 'tours.postCreator.steps.imageCropperFinish.content',
      position: 'bottom',
      aboveDrawer: true,
      awaitClick: '.btn-finish',
      blockOutsideSpotlight: true
    },
    // [13] First comment — prevToStep: 4 (back to media upload, not into branch path)
    {
      target: '.comment-post',
      title: 'tours.postCreator.steps.comment.title',
      content: 'tours.postCreator.steps.comment.content',
      position: 'right',
      prevToStep: 4,
      watchTargets: ['.comment-post .emoji-mart', '.form-overlay.select-mention'],
      lockScroll: '.container-form-scroll',
      blockOutsideSpotlight: true
    },
    // [14] Advanced settings
    {
      target: '.settings-advanced',
      title: 'tours.postCreator.steps.settings.title',
      content: 'tours.postCreator.steps.settings.content',
      position: 'right',
      spotlightPadding: 20,
      lockScroll: '.container-form-scroll',
      blockOutsideSpotlight: true
    },

    // [15] Music button — clickBeforeTarget: switch to TikTok/IG first
    //     Only shown for video + native TikTok/IG page; skipIfMissingToStep: 18 otherwise
    {
      target: '.form-network-tiktok .list-simple-btn .type-btn-simple, .form-network-instagram .list-simple-btn .type-btn-simple:not(.locked)',
      title: 'tours.postCreator.steps.musicBtn.title',
      content: 'tours.postCreator.steps.musicBtn.content',
      position: 'top',
      clickBeforeTarget: ['.nav-type-network .network.tiktok', '.nav-type-network .network.instagram'],
      skipIfMissingToStep: 18,
      awaitClick: '.form-network-tiktok .list-simple-btn .type-btn-simple, .form-network-instagram .list-simple-btn .type-btn-simple:not(.locked)',
      awaitClickDelegate: true,
      lockScroll: '.container-form-scroll',
      awaitClickAdvanceDelay: 400,
      blockOutsideSpotlight: true
    },
    // [16] Music overlay: search + top choices (steep2)
    {
      target: '.form-overlay.music-content.left .steep2',
      title: 'tours.postCreator.steps.musicOverlay.title',
      content: 'tours.postCreator.steps.musicOverlay.content',
      position: 'right',
      aboveDrawer: true,
      waitForTarget: true,
      awaitClick: '.form-overlay.music-content.left .steep2 .list-item .item',
      awaitClickDelegate: true,
      pulse: false,
      awaitClickAdvanceDelay: 600,
      hidePrev: true,
      blockOutsideSpotlight: true
    },
    // [17] Music overlay: selected track + cursor (steep1)
    {
      target: '.form-overlay.music-content.left',
      title: 'tours.postCreator.steps.musicSteep1.title',
      content: 'tours.postCreator.steps.musicSteep1.content',
      position: 'right',
      aboveDrawer: true,
      waitForTarget: true,
      awaitClick: '.form-overlay.music-content.left .btn-secondary',
      awaitClickAdvanceDelay: 200,
      hidePrev: true,
      blockOutsideSpotlight: true
    },
    // [18] Phone preview — prevToStep: 14 (not music overlay, which needs the overlay open)
    {
      target: '.container-phone',
      title: 'tours.postCreator.steps.preview.title',
      content: 'tours.postCreator.steps.preview.content',
      position: 'left',
      prevToStep: 14,
      blockOutsideSpotlight: true
    },
    // [19] Network tabs
    {
      target: '.nav-type-network',
      title: 'tours.postCreator.steps.networkNav.title',
      content: 'tours.postCreator.steps.networkNav.content',
      position: 'left',
      blockOutsideSpotlight: true
    },
    // [20] Action buttons
    {
      target: '.btns-action',
      title: 'tours.postCreator.steps.actions.title',
      content: 'tours.postCreator.steps.actions.content',
      position: 'bottom',
      blockTarget: true,
      blockOutsideSpotlight: true
    },
    // [21] Click preview button
    {
      target: '.btns-action .btn-secondary',
      title: 'tours.postCreator.steps.clickPreview.title',
      content: 'tours.postCreator.steps.clickPreview.content',
      position: 'left',
      awaitClick: '.btns-action .btn-secondary',
      blockOutsideSpotlight: true
    },
    // [22] Preview mode
    {
      target: '.container-phone',
      title: 'tours.postCreator.steps.previewMode.title',
      content: 'tours.postCreator.steps.previewMode.content',
      position: 'left',
      spotlightPadding: { top: 20, right: 100, bottom: 0, left: 20 },
      blockTarget: '.btns-action',
      hidePrev: true,
      blockOutsideSpotlight: true
    },
    // [23] Publish / schedule — chains to plannerReturn
    {
      target: '.btns-action',
      title: 'tours.postCreator.steps.clickPlanifier.title',
      content: 'tours.postCreator.steps.clickPlanifier.content',
      position: 'bottom',
      awaitClick: '.btns-action .btn-secondary',
      nextTour: 'plannerReturn',
      blockTargetSiblings: true,
      hideFinish: true,
      blockOutsideSpotlight: true
    }
  ]
}
